import { useState, useEffect } from "react";
import { useRecover } from "../../../lib/api/users.api";
import { PasswordField } from "./PasswordField";
import { RecoveryKeyModal } from "./RecoveryKeyModal";
import { Logo } from "../../../ui/icons";

const MIN_WORDS = 4;
const MIN_WORD_LENGTH = 3;
const MIN_PASSWORD = 12;

type PasswordMode = "passphrase" | "classic";

function isPassphraseValid(words: readonly string[]): boolean {
  const validWords = words.filter((w) => w.length >= MIN_WORD_LENGTH);
  return validWords.length >= MIN_WORDS;
}

export function RecoverFormComponent({
  onBack,
  onSuccess,
  initialEmail = "",
}: {
  readonly onBack: () => void;
  readonly onSuccess: () => void;
  readonly initialEmail?: string;
}) {
  const [email, setEmail] = useState(initialEmail);
  const [recoveryKey, setRecoveryKey] = useState("");
  const [newPassphraseWords, setNewPassphraseWords] = useState<string[]>([]);
  const [newPassword, setNewPassword] = useState("");
  const [passwordMode, setPasswordMode] = useState<PasswordMode>("passphrase");
  const [showPassword, setShowPassword] = useState(false);
  const [pendingRecoveryKey, setPendingRecoveryKey] = useState<string | null>(null);
  const recover = useRecover();

  useEffect(() => {
    const meta = document.createElement("meta");
    meta.name = "robots";
    meta.content = "noindex, nofollow";
    document.head.appendChild(meta);
    return () => {
      meta.remove();
    };
  }, []);

  const hasPassphrase = passwordMode === "passphrase" && isPassphraseValid(newPassphraseWords);
  const hasClassicPassword = passwordMode === "classic" && newPassword.length >= MIN_PASSWORD;
  const isValid = email.trim() && recoveryKey.trim() && (hasPassphrase || hasClassicPassword);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!isValid) return;
    const finalPassword = passwordMode === "passphrase"
      ? newPassphraseWords.join(" ")
      : newPassword;
    recover.mutate(
      {
        email: email.trim(),
        recoveryKey: recoveryKey.trim(),
        newPassword: finalPassword,
      },
      {
        onSuccess: (response) => {
          if (response.recoveryKey) {
            setPendingRecoveryKey(response.recoveryKey);
          } else {
            onSuccess();
          }
        },
      },
    );
  };

  if (pendingRecoveryKey) {
    return <RecoveryKeyModal recoveryKey={pendingRecoveryKey} onAcknowledge={onSuccess} />;
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-surface p-8">
      <div className="w-full max-w-sm flex flex-col gap-8">
        <div className="text-center flex flex-col gap-1">
          <div className="flex items-center justify-center gap-2">
            <Logo className="size-8" />
            <h1 className="text-3xl font-bold text-ink">Palnia</h1>
          </div>
          <p className="text-sm text-ink-muted">Récupération de compte</p>
        </div>

        <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-3">
          <p className="text-xs text-amber-400 leading-relaxed">
            Votre clé de récupération est indispensable pour retrouver l'accès à vos données chiffrées. Sans elle, vos données seront perdues.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col gap-3">
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Votre email"
            required
            autoComplete="email"
            autoFocus
            className="px-4 py-3 bg-surface-alt border border-border text-ink
              placeholder:text-ink-muted focus:outline-none focus:ring-2
              focus:ring-accent rounded"
          />
          <input
            type="text"
            value={recoveryKey}
            onChange={(e) => setRecoveryKey(e.target.value)}
            placeholder="Clé de récupération (XXXX-XXXX-...)"
            required
            autoComplete="off"
            className="px-4 py-3 bg-surface-alt border border-border text-ink font-mono
              placeholder:text-ink-muted placeholder:font-sans focus:outline-none
              focus:ring-2 focus:ring-accent rounded"
          />
          <PasswordField
            passwordMode={passwordMode}
            onPasswordModeChange={setPasswordMode}
            password={newPassword}
            onPasswordChange={setNewPassword}
            passphraseWords={newPassphraseWords}
            onPassphraseWordsChange={setNewPassphraseWords}
            showPassword={showPassword}
            onShowPasswordChange={setShowPassword}
            isRegister
          />

          <button
            type="submit"
            disabled={recover.isPending || !isValid}
            className="mt-1 px-4 py-3 bg-accent text-white font-semibold
              hover:opacity-80 disabled:opacity-40 focus:outline-none
              focus:ring-2 focus:ring-accent rounded transition-opacity"
          >
            {recover.isPending ? "Chargement..." : "Récupérer mon compte"}
          </button>

          {recover.isError && (
            <p className="text-xs text-center text-red-500">
              Clé de récupération invalide ou email incorrect.
            </p>
          )}
        </form>

        <p className="text-xs text-ink-muted text-center">
          <button onClick={onBack} className="text-accent hover:underline">
            Retour à la connexion
          </button>
        </p>
      </div>
    </div>
  );
}
